/**
 * A standalone re-implementation of the `js-cookie` package.
 *
 * Mirrors the public surface of js-cookie v3:
 *   - `Cookies.get()` / `Cookies.get(name)`
 *   - `Cookies.set(name, value, attributes?)`
 *   - `Cookies.remove(name, attributes?)`
 *   - `Cookies.withAttributes(attributes)`
 *   - `Cookies.withConverter(converter)`
 *   - `Cookies.attributes` and `Cookies.converter` (frozen)
 *
 * Adds back the v2 `Cookies.noConflict()` helper for completeness.
 */

export interface CookieAttributes {
	expires?: number | Date
	path?: string
	domain?: string
	secure?: boolean
	sameSite?: string
	[attribute: string]: unknown
}

export interface Converter {
	read: (value: string, name: string) => string
	write: (value: string, name: string) => string
}

export interface CookiesApi {
	readonly attributes: CookieAttributes
	readonly converter: Converter
	set: (
		name: string,
		value: unknown,
		attributes?: CookieAttributes,
	) => string | undefined
	get: ((name: string) => string | undefined) & (() => Record<string, string>)
	remove: (name: string, attributes?: CookieAttributes) => void
	withAttributes: (attributes: CookieAttributes) => CookiesApi
	withConverter: (converter: Partial<Converter>) => CookiesApi
	noConflict?: () => CookiesApi
}

function assign<T extends Record<string, unknown>>(
	target: T,
	...sources: Array<Record<string, unknown> | undefined>
): T {
	for (const source of sources) {
		if (!source) continue
		for (const key in source) {
			if (key === "__proto__") continue
			;(target as Record<string, unknown>)[key] = source[key]
		}
	}
	return target
}

const defaultConverter: Converter = {
	read(value: string): string {
		if (value[0] === '"') {
			value = value.slice(1, -1)
		}
		return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent)
	},
	write(value: string): string {
		return encodeURIComponent(value).replace(
			/%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
			decodeURIComponent,
		)
	},
}

function init(
	converter: Converter,
	defaultAttributes: CookieAttributes,
): CookiesApi {
	function set(
		name: string,
		value: unknown,
		attributes?: CookieAttributes,
	): string | undefined {
		if (typeof document === "undefined") {
			return
		}

		const merged: CookieAttributes = assign(
			{} as CookieAttributes,
			defaultAttributes as Record<string, unknown>,
			attributes as Record<string, unknown> | undefined,
		)

		if (typeof merged.expires === "number") {
			merged.expires = new Date(Date.now() + merged.expires * 864e5)
		}
		if (merged.expires instanceof Date) {
			merged.expires = merged.expires.toUTCString() as unknown as Date
		}

		const encodedName = encodeURIComponent(name)
			.replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
			.replace(/[()]/g, escape)

		let stringifiedAttributes = ""
		for (const attributeName in merged) {
			const attributeValue = merged[attributeName]
			if (!attributeValue) continue

			stringifiedAttributes += "; " + attributeName

			if (attributeValue === true) continue

			// RFC 6265 §5.2: stop at the first ';' in an attribute value.
			stringifiedAttributes += "=" + String(attributeValue).split(";")[0]
		}

		const written =
			encodedName +
			"=" +
			converter.write(value as string, encodedName) +
			stringifiedAttributes
		document.cookie = written
		return written
	}

	function get(name?: string): string | Record<string, string> | undefined {
		if (typeof document === "undefined") return
		if (arguments.length > 0 && !name) return

		const cookies = document.cookie ? document.cookie.split("; ") : []
		const jar: Record<string, string> = {}
		for (const cookie of cookies) {
			const parts = cookie.split("=")
			const value = parts.slice(1).join("=")

			try {
				const found = decodeURIComponent(parts[0])
				if (!(found in jar)) jar[found] = converter.read(value, found)
				if (name === found) break
			} catch {
				// malformed cookie name/value — skip
			}
		}

		return name ? jar[name] : jar
	}

	const api = Object.create(
		{
			set,
			get,
			remove(name: string, attributes?: CookieAttributes): void {
				set(
					name,
					"",
					assign(
						{} as CookieAttributes,
						attributes as Record<string, unknown> | undefined,
						{ expires: -1 } as Record<string, unknown>,
					),
				)
			},
			withAttributes(
				this: CookiesApi,
				attributes: CookieAttributes,
			): CookiesApi {
				return init(
					this.converter,
					assign(
						{} as CookieAttributes,
						this.attributes as Record<string, unknown>,
						attributes as Record<string, unknown>,
					),
				)
			},
			withConverter(
				this: CookiesApi,
				partial: Partial<Converter>,
			): CookiesApi {
				return init(
					assign(
						{} as Record<string, unknown>,
						this.converter as unknown as Record<string, unknown>,
						partial as Record<string, unknown>,
					) as unknown as Converter,
					this.attributes,
				)
			},
		},
		{
			attributes: { value: Object.freeze(defaultAttributes) },
			converter: { value: Object.freeze(converter) },
		},
	) as CookiesApi

	return api
}

const Cookies: CookiesApi = init(defaultConverter, { path: "/" })

if (typeof window !== "undefined") {
	const win = window as unknown as Record<string, unknown>
	const OldCookies = win.Cookies
	Cookies.noConflict = function () {
		win.Cookies = OldCookies
		return Cookies
	}
}

export default Cookies

export function get(name: string): string | undefined {
	return Cookies.get(name) as string | undefined
}

export function set(
	name: string,
	value: unknown,
	attributes?: CookieAttributes,
): string | undefined {
	return Cookies.set(name, value, attributes)
}

export function remove(name: string, attributes?: CookieAttributes): void {
	Cookies.remove(name, attributes)
}
